// src/App.jsx
import React, { Suspense, lazy, useEffect, useMemo, useState } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

import LanguageBar from "./components/LanguageBar";
import InAppCommunicationLayer from "./components/InAppCommunicationLayer";
import Intake from "./Intake";
import JourneyOverview from "./JourneyOverview";
import JourneyFullJournal from "./JourneyFullJournal";
import JourneyFinalSummary from "./JourneyFinalSummary";
import HelpNow from "./pages/HelpNow";
import AboutJourneys from "./pages/AboutJourneys";
import LegalDocument from "./pages/LegalDocument";
import DisclaimerGate from "./pages/DisclaimerGate";
import Account from "./pages/Account";
import BooksLanding from "./pages/BooksLanding";
import InnerVoiceLanding from "./pages/InnerVoiceLanding";
import SiteHubUnified from "./pages/SiteHubUnified";
import SiteHubUnifiedBalanced from "./pages/SiteHubUnifiedBalanced";
import OwnerAccess from "./pages/OwnerAccess";
import EntryReady from "./pages/EntryReady";
import JourneyEntryGateway from "./pages/JourneyEntryGateway";
import Phase1GateSelection from "./components/Phase1GateSelection";
import AuthLogin from "./auth/AuthLogin";
import AuthRegister from "./auth/AuthRegister";
import AuthGate from "./auth/AuthGate";
import { dirForLang, normalizeLang } from "./i18n";
import JourneyService from "./services/JourneyService";
import { getSystemStrings } from "./strings/systemStrings";

const ProblemSelection = lazy(() => import("./components/ProblemSelection"));
const JourneyDayDna = lazy(() => import("./pages/JourneyDayDna"));
const JourneyDayTransition = lazy(() => import("./pages/JourneyDayTransition"));
const JourneyLanguageMirror = lazy(() => import("./pages/JourneyLanguageMirror"));
const UnifiedChat = lazy(() => import("./pages/chat/UnifiedChat"));
const VoiceChat = lazy(() => import("./pages/VoiceChat/VoiceChat"));
const LiveMode = lazy(() => import("./pages/LiveMode"));
const Paywall = lazy(() => import("./pages/Paywall"));
const Phase1Home = lazy(() => import("./pages/Phase1Home"));
const JourneyGuidedDiscovery = lazy(() => import("./pages/JourneyGuidedDiscovery"));
const JourneyRecognition = lazy(() => import("./pages/JourneyRecognition"));
const AdminApp = lazy(() => import("./admin/AdminApp"));

function RouteFallback() {
  return (
    <div className="route-loading" role="status" aria-live="polite" aria-busy="true">
      טוען… / Loading…
    </div>
  );
}

function Lazy({ children }) {
  return <Suspense fallback={<RouteFallback />}>{children}</Suspense>;
}

/** נתיבי /legal/* פחות מתנגשים עם תיקיות בשרת (Plesk) מ־/privacy ו־/terms */
function LegacyLegalRedirect({ to }) {
  const { search } = useLocation();
  return <Navigate to={`${to}${search}`} replace />;
}

function MobileRedirectToJourney({ children, lang, location }) {
  const qs = new URLSearchParams(location.search);
  /* רק ?mobile=1 בכתובת — לא localStorage: אחרת כל דפדפן שבעבר נכנס עם mobile נדחף ל־/journey ולא רואה דפי נחיתה */
  const isMobileWebView = qs.get("mobile") === "1";
  if (isMobileWebView) {
    const next = new URLSearchParams(location.search);
    next.set("mobile", "1");
    if (!next.get("lang")) next.set("lang", lang);
    return <Navigate to={`/journey?${next.toString()}`} replace />;
  }
  return children;
}

function JourneyProgressBar({ lang, location }) {
  const qs = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const problemKey = qs.get("problemKey");
  const runId = qs.get("runId");
  const show =
    location.pathname.startsWith("/journey/") &&
    !location.pathname.startsWith("/journey/select") &&
    !location.pathname.startsWith("/journey/ready") &&
    !!problemKey;

  const [progress, setProgress] = useState({ completed: 0, total: 0, pct: 0 });

  useEffect(() => {
    if (!show || !problemKey) return;

    const compute = () => {
      const meta = JourneyService.getJourneyMeta(lang, problemKey);
      const total = Number(meta?.totalDays || 0);
      const { days } = JourneyService.loadAllDayStates(lang, problemKey, total || undefined, {
        runId,
      });
      const completed = Object.values(days || {}).filter((st) => st && st.completed).length;
      const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
      setProgress({
        completed,
        total,
        pct: Math.max(0, Math.min(100, pct)),
      });
    };

    compute();
    const t = setInterval(compute, 1200);
    const onStorage = () => compute();
    window.addEventListener("storage", onStorage);
    return () => {
      clearInterval(t);
      window.removeEventListener("storage", onStorage);
    };
  }, [show, lang, problemKey, runId]);

  if (!show || !progress.total) return null;

  const copy = getSystemStrings(lang).progressBar;

  return (
    <div className="journey-progress-wrap" dir={lang === "he" ? "rtl" : "ltr"}>
      <div className="journey-progress-head">
        <span>{copy.title}</span>
        <span>
          {progress.completed}/{progress.total} {copy.unit}
        </span>
      </div>
      <div className="journey-progress-track" aria-hidden="true">
        <div className="journey-progress-fill" style={{ width: `${progress.pct}%` }} />
      </div>
    </div>
  );
}

function AppLayout() {
  const location = useLocation();
  const qs = new URLSearchParams(location.search);
  const isMobileApp =
    qs.get("mobile") === "1" ||
    (typeof window !== "undefined" && window.localStorage?.getItem("innervoice_mobile") === "1");

  /* WebView נכנס עם ?mobile=1 — ניווט פנימי לעיתים מסיר את הפרמטר; נשאיר מצב אפליקציה ב-localStorage */
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      if (new URLSearchParams(location.search).get("mobile") === "1") {
        window.localStorage.setItem("innervoice_mobile", "1");
      }
    } catch {
      /* ignore */
    }
  }, [location.search]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    if (isMobileApp) {
      document.documentElement.classList.add("innervoice-mobile-app");
      document.body?.classList.add("innervoice-mobile-app");
      const v = document.querySelector('meta[name="viewport"]');
      if (v) v.setAttribute("content", "width=device-width,initial-scale=1,viewport-fit=cover");
    } else {
      document.documentElement.classList.remove("innervoice-mobile-app");
      document.body?.classList.remove("innervoice-mobile-app");
    }
  }, [isMobileApp]);
  const lang = normalizeLang(
    qs.get("lang") ||
      (typeof window !== "undefined" ? window.localStorage.getItem("innervoice_lang") : "he")
  );
  const dir = dirForLang(lang);

  const hideHeader =
    location.pathname === "/" ||
    location.pathname === "/unified-homepage-staging" ||
    location.pathname === "/innervoice" ||
    location.pathname === "/book" ||
    location.pathname === "/books" ||
    location.pathname === "/owner" ||
    location.pathname === "/journey" ||
    location.pathname === "/journey/select" ||
    location.pathname === "/journey/discovery" ||
    location.pathname === "/journey/recognition" ||
    location.pathname.startsWith("/admin");

  return (
    <div className={`app-root${isMobileApp ? " app-root--mobile" : ""}`} dir={dir}>
      {!hideHeader && (
        <header className="app-header">
          <LanguageBar />
        </header>
      )}

      <main className="app-main">
        <InAppCommunicationLayer />
        <JourneyProgressBar lang={lang} location={location} />
        <Routes>
          <Route
            path="/"
            element={
              <MobileRedirectToJourney lang={lang} location={location}>
                <SiteHubUnifiedBalanced />
              </MobileRedirectToJourney>
            }
          />
          <Route
            path="/unified-homepage-staging"
            element={
              <MobileRedirectToJourney lang={lang} location={location}>
                <SiteHubUnifiedBalanced />
              </MobileRedirectToJourney>
            }
          />
          <Route
            path="/owner"
            element={<OwnerAccess />}
          />
          <Route
            path="/innervoice"
            element={
              <MobileRedirectToJourney lang={lang} location={location}>
                <InnerVoiceLanding />
              </MobileRedirectToJourney>
            }
          />
          <Route
            path="/book"
            element={
              <MobileRedirectToJourney lang={lang} location={location}>
                <BooksLanding />
              </MobileRedirectToJourney>
            }
          />
          <Route
            path="/books"
            element={
              <MobileRedirectToJourney lang={lang} location={location}>
                <BooksLanding />
              </MobileRedirectToJourney>
            }
          />

          <Route path="/auth/login" element={<AuthLogin />} />
          <Route path="/auth/register" element={<AuthRegister />} />

          <Route
            path="/journey"
            element={
              <AuthGate>
                <JourneyEntryGateway />
              </AuthGate>
            }
          />
          <Route
            path="/journey/select"
            element={
              <AuthGate>
                <JourneyEntryGateway />
              </AuthGate>
            }
          />
          <Route
            path="/journey/discovery"
            element={
              <AuthGate>
                <Lazy>
                  <JourneyGuidedDiscovery />
                </Lazy>
              </AuthGate>
            }
          />
          <Route
            path="/journey/recognition"
            element={
              <AuthGate>
                <Lazy>
                  <JourneyRecognition />
                </Lazy>
              </AuthGate>
            }
          />
          <Route
            path="/journey/explorer"
            element={
              <AuthGate>
                <Lazy>
                  <ProblemSelection />
                </Lazy>
              </AuthGate>
            }
          />
          <Route
            path="/journey/ready"
            element={
              <AuthGate>
                <EntryReady />
              </AuthGate>
            }
          />

          <Route
            path="/journey/intake"
            element={
              <AuthGate>
                <Intake />
              </AuthGate>
            }
          />
          <Route
            path="/journey/disclaimer"
            element={
              <AuthGate>
                <DisclaimerGate />
              </AuthGate>
            }
          />
          <Route path="/help-now" element={<HelpNow />} />
          <Route
            path="/live-mode"
            element={
              <AuthGate>
                <Lazy>
                  <LiveMode />
                </Lazy>
              </AuthGate>
            }
          />
          <Route
            path="/paywall"
            element={
              <AuthGate>
                <Lazy>
                  <Paywall />
                </Lazy>
              </AuthGate>
            }
          />
          <Route path="/about" element={<AboutJourneys />} />
          <Route path="/legal/privacy" element={<LegalDocument kind="privacy" />} />
          <Route path="/legal/terms" element={<LegalDocument kind="terms" />} />
          <Route path="/privacy" element={<LegacyLegalRedirect to="/legal/privacy" />} />
          <Route path="/terms" element={<LegacyLegalRedirect to="/legal/terms" />} />
          <Route
            path="/account"
            element={
              <AuthGate>
                <Account />
              </AuthGate>
            }
          />

          <Route
            path="/journey/overview"
            element={
              <AuthGate>
                <JourneyOverview />
              </AuthGate>
            }
          />

          <Route
            path="/journey/day"
            element={
              <AuthGate>
                <Lazy>
                  <JourneyDayDna />
                </Lazy>
              </AuthGate>
            }
          />
          <Route
            path="/journey/day-dna"
            element={
              <AuthGate>
                <Lazy>
                  <JourneyDayDna />
                </Lazy>
              </AuthGate>
            }
          />
          <Route
            path="/journey/day-transition"
            element={
              <AuthGate>
                <Lazy>
                  <JourneyDayTransition />
                </Lazy>
              </AuthGate>
            }
          />
          <Route
            path="/journey/language-mirror"
            element={
              <AuthGate>
                <Lazy>
                  <JourneyLanguageMirror />
                </Lazy>
              </AuthGate>
            }
          />

          <Route
            path="/journey/journal"
            element={
              <AuthGate>
                <JourneyFullJournal />
              </AuthGate>
            }
          />
          <Route
            path="/journey/summary-final"
            element={
              <AuthGate>
                <JourneyFinalSummary />
              </AuthGate>
            }
          />

          <Route
            path="/phase1"
            element={
              <AuthGate>
                <Lazy>
                  <Phase1Home />
                </Lazy>
              </AuthGate>
            }
          />
          <Route
            path="/phase1/select"
            element={
              <AuthGate>
                <Phase1GateSelection />
              </AuthGate>
            }
          />
          <Route
            path="/phase1/ready"
            element={
              <AuthGate>
                <EntryReady />
              </AuthGate>
            }
          />

          <Route
            path="/chat"
            element={
              <AuthGate>
                <Lazy>
                  <UnifiedChat />
                </Lazy>
              </AuthGate>
            }
          />

          <Route
            path="/voice-chat"
            element={
              <AuthGate>
                <Lazy>
                  <VoiceChat />
                </Lazy>
              </AuthGate>
            }
          />

          <Route
            path="/admin/*"
            element={
              <Lazy>
                <AdminApp />
              </Lazy>
            }
          />

          <Route
            path="*"
            element={<Navigate to={`/journey?lang=${encodeURIComponent(lang)}`} replace />}
          />
        </Routes>
      </main>
    </div>
  );
}

export default function App() {
  return <AppLayout />;
}

